import { getAccessToken, logout } from "./auth";

// Strip trailing slash — SST includes the stage suffix with a trailing slash
const BASE_URL = (import.meta.env.VITE_API_URL as string).replace(/\/$/, "");

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Registered by App once the router + auth context are ready.
// Called when any API response returns 403 so the app can refresh permissions and redirect.
let forbiddenCallback: (() => void) | null = null;

export function setForbiddenHandler(fn: (() => void) | null): void {
  forbiddenCallback = fn;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = await getAccessToken();

  const url = new URL(`${BASE_URL}${path}`);
  if (options.params) {
    for (const [k, v] of Object.entries(options.params)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }

  const makeRequest = async (accessToken: string | null) => {
    return fetch(url.toString(), {
      method: options.method ?? "GET",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
  };

  let res = await makeRequest(token);

  // If 401, attempt a token refresh and retry once
  if (res.status === 401) {
    const freshToken = await getAccessToken({ forceRefresh: true });
    if (!freshToken) {
      // Refresh failed — clear Amplify session then redirect so the next
      // sign-in attempt doesn't hit "There is already a signed in user".
      await logout().catch(() => undefined);
      window.location.href = "/login";
      throw new ApiError(401, "SESSION_EXPIRED", "Your session has expired. Please log in again.");
    }
    res = await makeRequest(freshToken);
    if (res.status === 401) {
      await logout().catch(() => undefined);
      window.location.href = "/login";
      throw new ApiError(401, "SESSION_EXPIRED", "Your session has expired. Please log in again.");
    }
  }

  if (res.status === 204) return undefined as T;

  const json = (await res.json()) as { data?: T; error?: { code: string; message: string } };

  if (!res.ok) {
    if (res.status === 403) {
      forbiddenCallback?.();
    }
    throw new ApiError(
      res.status,
      json.error?.code ?? "UNKNOWN",
      json.error?.message ?? "An error occurred",
    );
  }

  return json.data as T;
}
